import Microgame from '../engine/Microgame.js';

export default class SawGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Scie !`, en: `Saw!`, es: `¡Sierra!` },
            controls: { fr: `Fais des allers-retours rapides au milieu de l'écran`, en: `Swipe back and forth quickly in the middle`, es: `Desliza adelante y atrás rápidamente en el medio` },
            objective: { fr: `Coupe complètement la planche avec la scie`, en: `Cut completely through the wood with the saw`, es: `Corta completamente la madera con la sierra` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--secondary-color)" stroke-width="2"><path d="m3 11 18-5v12L3 14v-3Z"/><path d="m3 11 2 2 2-2 2 2 2-2 2 2 2-2 2 2 3-3"/></svg>`,
            actions: [
                { type: 'swipe', fromX: 35, fromY: 35, toX: 65, toY: 35, duration: 180 },
                { type: 'swipe', fromX: 65, fromY: 35, toX: 35, toY: 35, duration: 180 },
                { type: 'swipe', fromX: 35, fromY: 38, toX: 65, toY: 38, duration: 180 },
                { type: 'swipe', fromX: 65, fromY: 38, toX: 35, toY: 38, duration: 180 },
                { type: 'wait', duration: 320 },
            ],
            loopDuration: 3000
        };
    }
    constructor(engine) {
        super(engine);
        this.saw = null;
        this.log = null;
        this.progress = 0;
        this.sawPhase = 0;
        this.lastMouseX = 0;
        this.baseSawY = 30;

        // Difficulty scaling
        const speedMult = engine.speedMultiplier || 1.0;
        
        // Objective: at speedMult = 4.0, we actually want the game math to think we afford the time of a 2.4 multiplier!
        const effectiveMult = 1 + (speedMult - 1) * (1.4 / 3);
        // Base time was historically 3000ms. 
        // We trick GameEngine's internal division (3000 / speedMult) by supplying a proportional duration:
        this.duration = 3000 * (speedMult / effectiveMult);
    }

    setup() {
        this.engine.ui.instruction.textContent = "SAW!";

        // Log (Primary)
        this.log = document.createElement('div');
        this.log.style.position = 'absolute';
        this.log.style.width = '150px';
        this.log.style.height = '150px';
        this.log.style.background = this.primaryColor;
        this.log.style.border = `4px solid ${this.secondaryColor}`;
        this.log.style.borderRadius = '50%';
        this.log.style.top = '50%';
        this.log.style.left = '50%';
        this.log.style.transform = 'translate(-50%, -50%)';
        this.log.style.boxShadow = `0 0 20px ${this.primaryColor}`; // Neon Log

        // Rings (Secondary)
        const ring = document.createElement('div');
        ring.style.position = 'absolute';
        ring.style.width = '100px';
        ring.style.height = '100px';
        ring.style.border = `2px solid ${this.secondaryColor}`;
        ring.style.opacity = '0.3';
        ring.style.borderRadius = '50%';
        ring.style.top = '50%';
        ring.style.left = '50%';
        ring.style.transform = 'translate(-50%, -50%)';
        this.log.appendChild(ring);

        const ring2 = ring.cloneNode();
        ring2.style.width = '50px';
        ring2.style.height = '50px';
        this.log.appendChild(ring2);

        this.container.appendChild(this.log);

        // Saw (Secondary Blade)
        this.saw = document.createElement('div');
        this.saw.style.position = 'absolute';
        this.saw.style.width = '220px';
        this.saw.style.height = '50px';
        this.saw.style.background = this.secondaryColor;
        this.saw.style.top = `${this.baseSawY}%`;
        this.saw.style.left = '50%';
        this.saw.style.transform = 'translate(-50%, -50%)';
        this.saw.style.borderRadius = '5px';
        this.saw.style.boxShadow = `0 0 15px ${this.secondaryColor}`; // Neon Saw

        // Teeth (Primary Pattern)
        const teeth = document.createElement('div');
        teeth.style.position = 'absolute';
        teeth.style.width = '100%';
        teeth.style.height = '15px';
        teeth.style.bottom = '-15px';
        teeth.style.background = `
            linear-gradient(45deg, transparent 75%, ${this.secondaryColor} 75%),
            linear-gradient(-45deg, transparent 75%, ${this.secondaryColor} 75%)
        `;
        teeth.style.backgroundSize = '20px 20px';
        teeth.style.backgroundPosition = '0 0, 10px 0';
        this.saw.appendChild(teeth);

        // Handle (Primary)
        const handle = document.createElement('div');
        handle.style.position = 'absolute';
        handle.style.width = '40px';
        handle.style.height = '70px';
        handle.style.background = this.primaryColor;
        handle.style.top = '-30px';
        handle.style.left = '-30px';
        handle.style.borderRadius = '8px';
        handle.style.border = `2px solid ${this.secondaryColor}`;
        handle.style.boxShadow = `0 0 10px ${this.primaryColor}`; // Neon Handle
        this.saw.appendChild(handle);

        this.container.appendChild(this.saw);

        this.onMove = this.onMove.bind(this);
        this.engine.input.element.addEventListener('pointermove', this.onMove);
    }

    onMove(e) {
        if (!this.active || this.won) return;

        const deltaX = Math.abs(e.clientX - this.lastMouseX);
        this.lastMouseX = e.clientX;

        if (deltaX > 0) {
            this.sawPhase += deltaX * 0.05;

            const offsetX = Math.sin(this.sawPhase) * 60;
            this.saw.style.transform = `translate(calc(-50% + ${offsetX}px), -50%)`;

            this.progress += deltaX * 0.025;

            const targetY = this.baseSawY + (this.progress / 100) * 40;
            this.saw.style.top = `${targetY}%`;

            if (Math.random() < 0.3) {
                this.createSawdust();
            }

            // Check if Saw Clears Log
            // Log Bottom: 50% + 75px
            // Saw Top: targetY%
            const containerHeight = this.container.clientHeight;
            const logBottomPx = (containerHeight * 0.5) + 75;
            const sawTopPx = (containerHeight * (targetY / 100));

            // Add a small buffer (e.g. 10px) to ensure it looks cleared
            if (sawTopPx > logBottomPx - 10) {
                this.finish();
            }
        }
    }

    finish() {
        if (this.won) return;

        this.log.style.transition = 'all 0.5s';
        this.log.style.opacity = '0';

        const leftHalf = this.log.cloneNode(true);
        leftHalf.className = 'saw-residue';
        leftHalf.style.opacity = '1';
        leftHalf.style.clipPath = 'inset(0 50% 0 0)';
        leftHalf.style.transform = 'translate(-60%, -50%) rotate(-15deg)';
        leftHalf.style.boxShadow = `0 0 20px ${this.primaryColor}`; // Neon Residue
        this.container.appendChild(leftHalf);

        const rightHalf = this.log.cloneNode(true);
        rightHalf.className = 'saw-residue';
        rightHalf.style.opacity = '1';
        rightHalf.style.clipPath = 'inset(0 0 0 50%)';
        rightHalf.style.transform = 'translate(-40%, -50%) rotate(15deg)';
        rightHalf.style.boxShadow = `0 0 20px ${this.primaryColor}`; // Neon Residue
        this.container.appendChild(rightHalf);

        this.saw.style.display = 'none';

        this.win();
    }

    createSawdust() {
        const dust = document.createElement('div');
        dust.className = 'saw-residue';
        dust.style.position = 'absolute';
        dust.style.width = '8px';
        dust.style.height = '8px';
        dust.style.background = this.secondaryColor;
        dust.style.boxShadow = `0 0 5px ${this.secondaryColor}`; // Neon Dust

        const sawRect = this.saw.getBoundingClientRect();
        const containerRect = this.container.getBoundingClientRect();
        const sawX = sawRect.left + sawRect.width / 2 - containerRect.left;
        const sawY = sawRect.top + sawRect.height - containerRect.top;

        dust.style.left = `${sawX}px`;
        dust.style.top = `${sawY}px`;
        dust.style.transform = `translate(${Math.random() * 40 - 20}px, ${Math.random() * 20}px)`;
        dust.style.transition = 'all 0.5s';
        dust.style.borderRadius = '2px';

        this.container.appendChild(dust);

        requestAnimationFrame(() => {
            dust.style.top = `${sawY + 150}px`;
            dust.style.opacity = '0';
            dust.style.transform += ` rotate(${Math.random() * 360}deg)`;
        });

        setTimeout(() => {
            if (dust.parentNode) dust.remove();
        }, 500);
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointermove', this.onMove);

        const residues = this.container.querySelectorAll('.saw-residue');
        residues.forEach(el => el.remove());
    }
}
